import type { KyInstance } from 'ky';

import { ensureError, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { TwentileCountsByMailing } from './types/twentile-counts-by-mailing.type';

/**
 * Retrieves the twentile count data for a specific mailing.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param mailingId - The unique identifier of the mailing for which to retrieve twentile count data.
 * @returns A promise that resolves to an `OperationResultWithData` containing a record where each key is the mail
 * code and maps to a `TwentileCountsByMailing` object.
 * @throws Will throw an error if the `mailingId` is not defined or if the HTTP request fails.
 */
export const getTwentileCountsByMailing = async (instance: KyInstance, mailingId?: number) => {
  try {
    if (isNotDefined(mailingId)) {
      throw new Error('Mailing ID is required to retrieve twentile count data.');
    }

    return await instance
      .get<OperationResultWithData<Record<string, TwentileCountsByMailing>>>(Uri.nth.twentileCount, {
        searchParams: { mailingId },
      })
      .json();
  } catch (e) {
    throw ensureError(e);
  }
};
